import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

// Connects to data-controller="response-time-chart"
export default class extends Controller {
  static values = {
    monitorId: Number,
    period: String
  }

  connect() {
    this.loadChartData()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async loadChartData() {
    try {
      const response = await fetch(`/monitors/${this.monitorIdValue}/stats/response_times?period=${this.periodValue}`)
      const data = await response.json()

      if (data.data && data.data.length > 0) {
        this.renderChart(data.data)
      } else {
        this.showEmptyState()
      }
    } catch (error) {
      console.error('Error fetching chart data:', error)
      this.showErrorState()
    }
  }

  renderChart(data) {
    // Create canvas element
    const canvas = document.createElement('canvas')
    this.element.innerHTML = ''
    this.element.appendChild(canvas)

    const ctx = canvas.getContext('2d')

    // Extract labels and values
    const labels = data.map(item => item.x)
    const values = data.map(item => item.y)
    const statuses = data.map(item => item.status)

    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, 256)
    gradient.addColorStop(0, 'rgba(99, 102, 241, 0.3)')
    gradient.addColorStop(1, 'rgba(99, 102, 241, 0)')

    // Point colors based on status
    const pointColors = statuses.map(status =>
      status === 'success' ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
    )

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Response Time (ms)',
          data: values,
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: gradient,
          tension: 0.3,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: 'rgba(99, 102, 241, 0.5)',
            borderWidth: 1,
            callbacks: {
              label: function(context) {
                const status = statuses[context.dataIndex]
                const statusText = status === 'success' ? '(Success)' : '(Failed)'
                return `${context.parsed.y}ms ${statusText}`
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Response Time (ms)'
            },
            ticks: {
              precision: 0
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Time'
            },
            ticks: {
              maxTicksLimit: 8,
              maxRotation: 45
            },
            grid: {
              display: false
            }
          }
        }
      }
    })
  }

  showEmptyState() {
    this.element.innerHTML = `
      <div class="flex items-center justify-center h-full text-base-content/60">
        <div class="text-center">
          <svg class="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          <p>No data available for this period</p>
        </div>
      </div>
    `
  }

  showErrorState() {
    this.element.innerHTML = `
      <div class="flex items-center justify-center h-full text-error/60">
        <div class="text-center">
          <svg class="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <p>Error loading chart data</p>
        </div>
      </div>
    `
  }
}
